import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink } from 'react-router-dom';
import { authApi } from '@/features/auth/api/auth.api';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { pickPostLoginRoute } from '@/features/auth/utils/handlePostAuth';
import { STORAGE_KEYS } from '@/utils/constants';
import { legalConfig } from '@/shared/config/legal';
import type { AuthResponse } from '@/types';
import { PhoneAuthForm } from './PhoneAuthForm';
import { OTPVerifyForm } from './OTPVerifyForm';
import { BrandLogo } from '@/shared/components/BrandLogo';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  onAuthSuccess: (route: string) => void;
  title?: string;
  subtitle?: string;
  /** Landing-page search flow: post-auth route prefers /guides. */
  travelerSearchFlow?: boolean;
}

type Step = 'phone' | 'otp';

const linkSx = {
  color: 'primary.main',
  fontWeight: 600,
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline', textUnderlineOffset: 3 },
} as const;

export function AuthModal({
  open,
  onClose,
  onAuthSuccess,
  title = 'Sign in to continue',
  subtitle = 'Use your mobile number for a one-time code, or continue with Google.',
  travelerSearchFlow = false,
}: AuthModalProps) {
  const [step, setStep] = useState<Step>('phone');
  const [otpPhone, setOtpPhone] = useState('');
  const { setAuth, setMe } = useAuth();

  const resetSteps = () => {
    setStep('phone');
    setOtpPhone('');
  };

  const handleClose = () => {
    resetSteps();
    onClose();
  };

  const handleOtpSent = (phone: string) => {
    localStorage.setItem(STORAGE_KEYS.OTP_PHONE, phone);
    setOtpPhone(phone);
    setStep('otp');
  };

  const finishAuth = async (data: AuthResponse) => {
    setAuth(data);
    let route = pickPostLoginRoute(
      {
        user: data.user,
        permissions: data.permissions,
        accountKind: data.accountKind ?? 'traveler',
        postAuthRoute: data.postAuthRoute ?? '/',
        requiresProfileSetup: data.requiresProfileSetup ?? false,
      },
      data
    );
    try {
      const me = await authApi.getMe();
      route = pickPostLoginRoute(me, data);
      setMe({ ...me, postAuthRoute: route });
    } catch {
      // proceed with verify-otp / google routing hints
    }
    resetSteps();
    onClose();
    onAuthSuccess(route);
  };

  /**
   * Persist the session; if profile setup is required, close this modal and let
   * TravelerProfileGate show the complete-profile dialog (non-dismissible).
   */
  const handleAuthResult = async (data: AuthResponse) => {
    if (data.requiresProfileSetup) {
      setAuth(data);
      resetSteps();
      onClose();
      return;
    }
    await finishAuth(data);
  };

  const handleBack = () => {
    setStep('phone');
    setOtpPhone('');
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="body"
      aria-label="Sign in"
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: { xs: 'calc(100% - 32px)', sm: 440 },
          m: { xs: 2, sm: 3 },
          borderRadius: { xs: 3, sm: 'var(--radius-xl)' },
          bgcolor: 'background.paper',
          boxShadow: '0 24px 80px rgba(15, 23, 42, 0.18)',
          overflow: 'hidden',
          backgroundImage: 'none',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: 'rgba(15, 23, 42, 0.45)',
            backdropFilter: 'blur(4px)',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2.5, sm: 3 },
          pt: { xs: 2, sm: 2.25 },
          pb: 0.5,
        }}
      >
        <BrandLogo size="sm" surface="light" />
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="Close sign in"
          sx={{
            color: 'text.secondary',
            bgcolor: 'rgba(15, 23, 42, 0.04)',
            '&:hover': { bgcolor: 'rgba(15, 23, 42, 0.08)', color: 'text.primary' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2.5, sm: 3.5 },
          pt: 1.25,
          pb: { xs: 2.5, sm: 3 },
          maxHeight: { xs: 'min(82dvh, 720px)', sm: 'min(85dvh, 760px)' },
          overflowY: 'auto',
        }}
      >
        {step === 'phone' ? (
          <PhoneAuthForm
            title={title}
            subtitle={subtitle}
            onOtpSent={handleOtpSent}
            onGoogleSuccess={handleAuthResult}
            travelerSearch={travelerSearchFlow}
            smsConsentSource="web_auth_modal"
            compact
          />
        ) : (
          <OTPVerifyForm
            phone={otpPhone}
            travelerSearch={travelerSearchFlow}
            onSuccess={handleAuthResult}
            onBack={handleBack}
          />
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          textAlign="center"
          sx={{
            mt: 2.25,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            lineHeight: 1.6,
            fontSize: '0.75rem',
          }}
        >
          By continuing, you agree to our{' '}
          <Box component={RouterLink} to={legalConfig.termsPath} sx={linkSx}>
            Terms
          </Box>{' '}
          and{' '}
          <Box component={RouterLink} to={legalConfig.privacyPolicyPath} sx={linkSx}>
            Privacy Policy
          </Box>
          .
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
